import React, { useContext, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, HelpCircle, PackageSearch } from "lucide-react";
import { createQuote, getUpcLookupByUpc } from "../../reducers/quote.slice";
import { AuthContext } from "../../context/authContext";
import upcImages from "../../assests/images/upc_image.png";
import PageHeader from "../../components/shared/PageHeader";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent } from "../../components/ui/dialog";

// Byte-for-byte port of QuoteFormPage's validation/dispatch logic — only the
// JSX changed.
export default function MemberQuoteFormPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showToast } = useContext(AuthContext);

  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    item_name: "",
    item_link: "",
    upc: "",
    quantity: 1,
    item_image_url: "",
  });
  const [errors, setErrors] = useState({});
  const [upcHelpOpen, setUpcHelpOpen] = useState(false);
  const [upcLookupImage, setUpcLookUpImage] = useState(null);

  const handleFormData = (name, value) => {
    let errorMsg = "";
    if (name === "quantity") {
      if (Number(value) < 1) errorMsg = "Values cannot be less than 1";
    }
    if (name === "upc") {
      if (!/^\d*$/.test(value)) {
        errorMsg = "UPC must contain only numbers";
      } else if (value.length > 0 && (value.length < 11 || value.length > 14)) {
        errorMsg = "UPC should be 11-14 digits long";
      }
    }
    setErrors((prev) => ({ ...prev, [name]: errorMsg }));
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleGetUpcData = () => {
    if (errors.upc) return;
    setSubmitting(true);
    dispatch(getUpcLookupByUpc({ upc: formData.upc })).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setSubmitting(false);
        showToast(res?.error?.message, "error");
        setFormData({ item_name: "", item_link: "", quantity: 1, item_image_url: "", upc: formData.upc });
        setUpcLookUpImage(null);
        return;
      }
      setSubmitting(false);
      const { title, item_url } = res?.payload || {};
      setUpcLookUpImage(res?.payload?.images?.[0]?.url || null);
      setFormData((prev) => ({
        ...prev,
        item_name: title || "",
        item_link: item_url || "",
        item_image_url: res?.payload?.images?.[0]?.url || "",
      }));
    });
  };

  const handleCreateQuote = () => {
    setSubmitting(true);
    const payload = { ...formData };
    if (!payload?.item_image_url) delete payload.item_image_url;
    dispatch(createQuote(payload)).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setSubmitting(false);
        showToast(res?.error?.message, "error");
        return;
      }
      setSubmitting(false);
      navigate(-1);
    });
  };

  const numericQuantitySanitize = (e) => {
    const invalidChars = ["e", "E", ".", ",", "+", "-"];
    if (
      invalidChars.includes(e.key) ||
      (!/^\d$/.test(e.key) &&
        !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab", "Enter", "Home", "End"].includes(e.key) &&
        !e.ctrlKey &&
        !e.metaKey)
    ) {
      e.preventDefault();
    }
  };

  const isSubmitDisabled =
    !formData.item_link ||
    !formData.item_name ||
    !formData.quantity ||
    !formData.upc ||
    !!errors.quantity ||
    parseInt(formData.quantity) === 0 ||
    submitting;

  return (
    <div style={{ maxWidth: 920, margin: "0 auto" }}>
      <button
        type="button"
        onClick={() => navigate(-1)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          background: "transparent",
          border: "none",
          color: "rgba(255,255,255,0.6)",
          fontSize: 13,
          cursor: "pointer",
          padding: 0,
          marginBottom: 20,
        }}
      >
        <ArrowLeft size={15} /> Back
      </button>

      <PageHeader title="Request Quote" subtitle="Enter the item details below to add it to your order." />

      <div style={{ display: "flex", flexWrap: "wrap", gap: 24, marginTop: 24 }}>
        {/* Preview panel — fills the space that used to sit empty until a
            UPC lookup resolved, and gives the fetched image real presence. */}
        <div
          style={{
            flex: "1 1 280px",
            minWidth: 280,
            border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: 18,
            padding: 28,
            background: "linear-gradient(165deg, rgba(0,173,181,0.07), rgba(255,255,255,0.02) 55%)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            minHeight: 420,
          }}
        >
          {upcLookupImage ? (
            <>
              <div
                style={{
                  width: "100%",
                  height: 240,
                  borderRadius: 14,
                  marginBottom: 20,
                  background: "linear-gradient(135deg, #f5f7fa, #e4e8f0)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  overflow: "hidden",
                }}
              >
                <img
                  src={upcLookupImage}
                  alt="UPC"
                  style={{ width: "100%", height: "100%", objectFit: "contain", mixBlendMode: "multiply" }}
                />
              </div>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#fff" }}>{formData.item_name || "Item found"}</div>
              <div style={{ fontSize: 12.5, color: "#c3c8cc", marginTop: 6 }}>Matched from UPC {formData.upc}</div>
            </>
          ) : (
            <>
              <div
                style={{
                  width: 84,
                  height: 84,
                  borderRadius: "50%",
                  background: "rgba(0,173,181,0.1)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: 20,
                }}
              >
                <PackageSearch size={34} color="#00ADB5" />
              </div>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#fff" }}>Look up your item</div>
              <div style={{ fontSize: 13, color: "#c3c8cc", marginTop: 8, lineHeight: 1.6, maxWidth: 260 }}>
                Enter a UPC and we'll try to match the item name, link, and image for you automatically.
              </div>
            </>
          )}
        </div>

        <div
          style={{
            flex: "2 1 380px",
            minWidth: 340,
            border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: 18,
            padding: 28,
            background: "linear-gradient(180deg, rgba(255,255,255,0.02), transparent)",
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <Label htmlFor="upc" style={{ marginBottom: 0, fontSize: 13.5 }}>
                  UPC
                </Label>
                <button
                  type="button"
                  onClick={() => setUpcHelpOpen(true)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    background: "transparent",
                    border: "none",
                    color: "#c3c8cc",
                    fontSize: 12,
                    cursor: "pointer",
                    padding: 0,
                  }}
                >
                  <HelpCircle size={13} /> Having trouble finding the UPC?
                </button>
              </div>
              <Input
                id="upc"
                value={formData.upc}
                placeholder="Enter UPC"
                inputMode="numeric"
                onChange={(e) => handleFormData("upc", e.target.value.replace(/\D/g, "").slice(0, 14))}
                onBlur={handleGetUpcData}
                onPaste={(e) => {
                  const pasteData = e.clipboardData.getData("text");
                  if (!/^\d+$/.test(pasteData)) e.preventDefault();
                }}
                style={errors.upc ? { border: "1px solid #f27059" } : undefined}
              />
              {errors.upc && <div style={{ fontSize: 11.5, color: "#f27059", marginTop: 6 }}>{errors.upc}</div>}
            </div>

            <div>
              <Label htmlFor="item_name" style={{ fontSize: 13.5 }}>
                Item Name
              </Label>
              <Input
                id="item_name"
                value={formData.item_name}
                placeholder="Item Name"
                onChange={(e) => handleFormData("item_name", e.target.value)}
              />
            </div>

            <div>
              <Label htmlFor="item_link" style={{ fontSize: 13.5 }}>
                Item Link
              </Label>
              <Input
                id="item_link"
                value={formData.item_link}
                placeholder="Enter Product Link"
                onChange={(e) => handleFormData("item_link", e.target.value)}
              />
            </div>

            <div style={{ maxWidth: 180 }}>
              <Label htmlFor="quantity" style={{ fontSize: 13.5 }}>
                Quantity
              </Label>
              <Input
                id="quantity"
                type="text"
                inputMode="numeric"
                value={formData.quantity}
                placeholder="Enter Quantity"
                onChange={(e) => handleFormData("quantity", e.target.value.replace(/\D/g, ""))}
                onKeyDown={numericQuantitySanitize}
                onPaste={(e) => {
                  const pasteData = e.clipboardData.getData("text");
                  if (!/^\d+$/.test(pasteData)) e.preventDefault();
                }}
                style={errors.quantity ? { border: "1px solid #f27059" } : undefined}
              />
              {errors.quantity && <div style={{ fontSize: 11.5, color: "#f27059", marginTop: 6 }}>{errors.quantity}</div>}
            </div>

            <Button onClick={handleCreateQuote} disabled={isSubmitDisabled} style={{ marginTop: 8, height: 46, fontSize: 14.5 }}>
              {submitting ? "Submitting…" : "Submit"}
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={upcHelpOpen} onOpenChange={setUpcHelpOpen}>
        <DialogContent style={{ maxWidth: 420, background: "rgba(0,0,0,0.9)", padding: 16 }}>
          <img src={upcImages} alt="UPC location example" style={{ width: "100%", borderRadius: 10 }} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
